// The simulation loop. `Sim` owns every piece of mutable state for one run - units, both pictures, the
// order book, the event log, the red cell - and advances them one 6-second tick at a time in a fixed
// order. Nothing here touches the DOM; the worker (src/sim/worker.ts) hosts it off the main thread and
// the client (src/sim/client.ts) talks to that worker. See spec §4 and §9, and plan Task 12.
//
// Determinism is the contract: `{ao, scenario, dials, seed, orderLog}` fully determines every tick, so
// `hash()` of two runs with the same inputs is identical and `Sim.replay` reproduces a past run exactly.

import type {
  CardId, Contact, Dials, Event, Graphic, Order, Picture, Placement, Posture, Scenario, Side, Snapshot,
  Target, Unit, UnitType,
} from './types';
import { TICKS_PER_MIN } from './types';
import { Grid } from './grid';
import { Rng } from './rng';
import { EventLog } from './events';
import { OrderBook } from './orders';
import { FlowFieldCache } from './flowfield';
import { graphicById } from './scenario';
import { stepMovement } from './movement';
import { newDetectionState, newPicture, stepDetection } from './detection';
import type { DetectionState } from './detection';
import { newCombatState, stepCombat } from './combat';
import type { CombatState } from './combat';
import { newLogisticsState, stepLogistics } from './logistics';
import type { LogisticsState } from './logistics';
import { RedCell } from './redcell';
import { loadRules, type Rules, type RulesOverrides, type RulesPreset } from './rules';

/** One ground-truth sample of every unit, recorded every 10 ticks so the debrief can replay what actually happened. */
export interface TruthFrame {
  tick: number;
  units: { id: number; x: number; y: number; strength: number; alive: boolean }[];
}

/** One entry of the order log a run records, sufficient (with the seed) to replay the run exactly. */
export interface OrderLogEntry {
  t: number; side: Side; unitId: number; card: CardId; target: Target; playId?: string;
}

/** Phase-line ids in advance order; the phase name is chosen by the furthest one any blue ground unit crossed. */
const PL_SEQUENCE = ['pl-red', 'pl-white', 'pl-blue'] as const;
const PHASE_NAMES = ['Before PL RED', 'PL RED–WHITE', 'PL WHITE–BLUE', 'Beyond PL BLUE'] as const;

/** Ticks the objective must be clear of living red before blue's presence counts as a seizure (5 sim minutes). */
const OBJ_CLEAR_TICKS = 5 * TICKS_PER_MIN;
/** Blue ground platoons that must be inside the objective for a seizure. */
const OBJ_BLUE_PLATOONS = 3;

/** Maneuver platoons for the "objective seized" test. */
const GROUND_PLATOON_TYPES: ReadonlySet<UnitType> = new Set<UnitType>(['armor', 'mech', 'inf']);

/** Everything that is not an airframe counts as a ground unit for phase and force readouts. */
function isGround(u: Unit): boolean { return u.type !== 'uas'; }

function isGroundPlatoon(u: Unit): boolean { return GROUND_PLATOON_TYPES.has(u.type); }

/** A fresh unit at full strength from a scenario placement. `redMoraleStart` is `rules.redMoraleStart`
 * (pitch 9a "morale parity"): blue always starts at 100; red starts at the same 100 by default too,
 * unless the active ruleset explicitly sets it lower, in which case the OPORD situation card discloses
 * it (see src/ui/opord.ts) rather than leaving it as a quiet, undisclosed asymmetry. */
function makeUnit(id: number, side: Side, p: Placement, redMoraleStart: number): Unit {
  return {
    id,
    side,
    type: p.type,
    callsign: p.callsign,
    x: p.x,
    y: p.y,
    strength: 100,
    suppression: 0,
    morale: side === 0 ? 100 : redMoraleStart,
    fuel: 100,
    ammo: 100,
    posture: 'hold' as Posture,
    alive: true,
    inContact: false,
    contactSince: -1,
    destCell: -1,
    orderId: -1,
    replanUntil: 0,
    brokenUntil: 0,
    uasAlive: false, // drones start grounded; the Launch UAS card puts an airframe up
    lastReportTick: 0,
    movedCm: 0,
    pinned: false,
    firedThisMin: false,
    crossedPls: [],
  };
}

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** FNV-1a 32-bit over a string, returned as 8 lowercase hex digits. */
function fnv1a(s: string): string {
  let h = FNV_OFFSET;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, FNV_PRIME) >>> 0;
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

export class Sim {
  t = 0;
  units: Unit[] = [];
  pictures: [Picture, Picture];
  orders: OrderBook;
  log = new EventLog();
  red: RedCell;
  ended: Snapshot['ended'] = null;
  phase: string = PHASE_NAMES[0];
  truth: TruthFrame[] = [];

  readonly graphics: Graphic[];
  /** The resolved ruleset this run plays under (pitch 10a); `rulesPreset`/`rulesModified` are what the
   * worker's 'ended' payload and the live top bar/debrief header read to show "Ruleset: <preset>[ (modified)]". */
  readonly rules: Rules;
  readonly rulesPreset: RulesPreset;
  readonly rulesModified: boolean;

  private rng: Rng;
  private ffc: FlowFieldCache;
  /** Combat's and logistics' own per-Sim state (fire-mission volleys/last-fire ticks; one-shot warning
   * flags) - held here, not module-level, so two Sims in one process (or a replay alongside a live run)
   * never share or collide on it. See combat.ts's CombatState / logistics.ts's LogisticsState. */
  private combatState: CombatState = newCombatState();
  private logisticsState: LogisticsState = newLogisticsState();
  private detectionState: DetectionState = newDetectionState();
  /** Consecutive ticks with no living red unit inside the objective circle. */
  private objClearTicks = 0;
  /** How far into the event log the culmination watch has read, so the check stays O(new events). */
  private logCursor = 0;
  private culminated = false;

  constructor(
    readonly grid: Grid,
    readonly scenario: Scenario,
    readonly dials: Dials,
    readonly seed: number,
    rulesPreset: RulesPreset = 'realistic',
    rulesOverrides?: RulesOverrides,
  ) {
    this.rng = new Rng(seed);
    this.rulesPreset = rulesPreset;
    this.rules = loadRules(rulesPreset, rulesOverrides);
    this.rulesModified = !!rulesOverrides && Object.keys(rulesOverrides).length > 0;
    // The order book gets its own generator so re-plan penalties never shift the sim's own roll stream.
    this.orders = new OrderBook(this.log, new Rng(seed + 1), this.rules);
    this.graphics = scenario.graphics;
    this.ffc = new FlowFieldCache(grid);
    this.pictures = [newPicture(0), newPicture(1)];
    this.red = new RedCell(scenario, dials, this.rules);

    let id = 0;
    for (const p of scenario.blue) this.units.push(makeUnit(id++, 0, p, this.rules.redMoraleStart));
    for (const p of scenario.red) this.units.push(makeUnit(id++, 1, p, this.rules.redMoraleStart));
  }

  /**
   * Advances one tick (6 sim seconds) in a fixed order: orders, movement, detection, combat, logistics,
   * red cell, then the phase and end-condition checks. A no-op once the run has ended.
   */
  tick(): void {
    if (this.ended !== null) return;
    const t = this.t;

    this.orders.tick(t, this.units);
    stepMovement(this.units, this.grid, this.ffc, this.dials, t, this.log, this.graphics);
    stepDetection(t, this.units, this.grid, this.pictures, this.dials, this.rng, this.log, this.graphics, this.detectionState, this.rules);
    stepCombat(t, this.units, this.grid, this.pictures, this.orders, this.dials, this.rng, this.log, this.combatState, this.rules);
    stepLogistics(t, this.units, this.dials, this.log, this.logisticsState, this.rules);
    if (t % TICKS_PER_MIN === 0) {
      this.red.step(t, this.units, this.pictures[1], this.orders, this.grid, this.graphics, this.log, this.rng);
    }

    this.updatePhase(t);
    this.checkEnd(t);
    if (t % TICKS_PER_MIN === 0) this.recordTruth(t);

    this.t = t + 1;
  }

  /** Issues an order on the given side's command net at the current tick. */
  issue(side: Side, unitId: number, card: CardId, target: Target, playId?: string): Order {
    return this.orders.issue(
      this.t, side, unitId, card, target, this.units, this.grid, this.graphics, this.dials, playId, this.pictures[side],
    );
  }

  /** Stands a unit down. The side is taken from the unit itself (blue when the unit is unknown). */
  cancel(unitId: number): Order {
    const unit = this.units.find((u) => u.id === unitId);
    return this.orders.cancel(this.t, unit ? unit.side : 0, unitId, this.units, this.dials);
  }

  /**
   * The blue-visible view of the world right now: ground truth for blue's own units (the UI draws what the
   * staff believes it has), but contacts and reported friendly positions filtered to what has actually
   * arrived at headquarters by this tick.
   *
   * `units` carries blue's own force and nothing else. Red reaches the client only through
   * `picture.contacts` - the reports blue actually has, with their age and their error - which is what
   * "the player fights the picture, not the truth" means at the wire. The snapshot used to include every
   * red unit's true position ten times a second, so anyone with a console had perfect intelligence.
   *
   * `includeEnemy` is for the two consumers that are entitled to ground truth and are not the live
   * client: the end-of-run payload the debrief is built from, and the worker's debug-only `truth`
   * message (src/sim/worker.ts).
   */
  snapshot(opts: { includeEnemy?: boolean } = {}): Snapshot {
    const contacts: Contact[] = [];
    for (const c of this.pictures[0].contacts.values()) {
      if (c.reportedAt <= this.t) contacts.push({ ...c });
    }
    contacts.sort((a, b) => a.enemyId - b.enemyId);

    const reported: [number, { x: number; y: number; tick: number }][] = [];
    for (const [id, r] of this.pictures[0].reported) {
      if (r.tick <= this.t) reported.push([id, { ...r }]);
    }
    reported.sort((a, b) => a[0] - b[0]);

    return {
      tick: this.t,
      units: this.units
        .filter((u) => opts.includeEnemy === true || u.side === 0)
        .map((u) => ({ ...u, crossedPls: [...u.crossedPls] })),
      picture: { contacts, reported },
      orders: this.orders.orders.map((o) => ({ ...o })),
      net: { occupancy: this.orders.occupancy(0, this.t), queueSec: this.orders.queueSec(0, this.t) },
      force: this.force(),
      phase: this.phase,
      ended: this.ended,
    };
  }

  /**
   * A 32-bit FNV-1a fingerprint of the whole simulation state: every unit's fields, every order's status,
   * and both generators' position in their streams - the sim's own, and (pitch 9e) the OrderBook's,
   * which rolls its own re-plan-penalty stream and previously went uncounted, so two runs that diverged
   * only in re-plan penalties could still hash identical. Two runs with the same inputs must produce the
   * same value.
   */
  hash(): string {
    const parts: string[] = [`t=${this.t}`, `end=${this.ended ?? '-'}`];
    for (const u of this.units) {
      parts.push([
        u.id, u.side, u.x, u.y, u.strength, u.suppression, u.morale, u.fuel, u.ammo,
        u.posture, u.alive ? 1 : 0, u.inContact ? 1 : 0, u.contactSince, u.destCell, u.orderId,
        u.replanUntil, u.brokenUntil, u.uasAlive ? 1 : 0, u.lastReportTick, u.movedCm,
        u.pinned ? 1 : 0, u.crossedPls.length,
      ].join(','));
    }
    for (const o of this.orders.orders) parts.push(`${o.id}:${o.status}`);
    parts.push(`rng=${this.rng.state().join(',')}`);
    parts.push(`obrng=${this.orders.rngState().join(',')}`);
    return fnv1a(parts.join('|'));
  }

  /** Re-runs a recorded order log from tick 0 up to (but not including) `untilTick`. `rulesPreset`/
   * `rulesOverrides` must match the ruleset the original run played under (same contract as `seed` and
   * `dials`) for the replay to reproduce it exactly. */
  static replay(
    grid: Grid,
    scenario: Scenario,
    dials: Dials,
    seed: number,
    orderLog: OrderLogEntry[],
    untilTick: number,
    rulesPreset: RulesPreset = 'realistic',
    rulesOverrides?: RulesOverrides,
  ): Sim {
    const sim = new Sim(grid, scenario, dials, seed, rulesPreset, rulesOverrides);
    const byTick = new Map<number, OrderLogEntry[]>();
    for (const e of orderLog) {
      const list = byTick.get(e.t);
      if (list) list.push(e); else byTick.set(e.t, [e]);
    }
    while (sim.t < untilTick) {
      // A run that reached one of its end conditions stops here. `tick()` is a no-op once `ended` is
      // set and never advances `t`, so without this a replay asked for a tick past the end of the run
      // - which is exactly what branching from a decision point can ask for (src/sim/branchRun.ts) -
      // spins forever instead of returning the finished fight.
      if (sim.ended !== null) break;
      const due = byTick.get(sim.t);
      if (due) {
        for (const e of due) {
          if (e.card === 'cancel') sim.cancel(e.unitId);
          else sim.issue(e.side, e.unitId, e.card, e.target, e.playId);
        }
      }
      sim.tick();
    }
    return sim;
  }

  // ---- internals ------------------------------------------------------------------------------------

  /** Averages over alive blue ground units; 0 when blue has no ground units left. */
  private force(): { strength: number; fuel: number; ammo: number } {
    let n = 0, strength = 0, fuel = 0, ammo = 0;
    for (const u of this.units) {
      if (u.side !== 0 || !u.alive || !isGround(u)) continue;
      n++; strength += u.strength; fuel += u.fuel; ammo += u.ammo;
    }
    if (n === 0) return { strength: 0, fuel: 0, ammo: 0 };
    return { strength: strength / n, fuel: fuel / n, ammo: ammo / n };
  }

  /** Recomputes the phase from the furthest phase line any alive blue ground unit has crossed. */
  private updatePhase(t: number): void {
    let furthest = 0;
    for (const u of this.units) {
      if (u.side !== 0 || !u.alive || !isGround(u)) continue;
      for (let i = PL_SEQUENCE.length - 1; i >= furthest; i--) {
        if (u.crossedPls.includes(PL_SEQUENCE[i])) { furthest = i + 1; break; }
      }
    }
    const name = PHASE_NAMES[Math.min(furthest, PHASE_NAMES.length - 1)];
    if (name !== this.phase) {
      this.phase = name;
      this.log.push(t, 'phase', 0, { phase: name });
    }
  }

  /** Objective seizure, culmination (flagged by logistics), and the scenario time limit, in that order. */
  private checkEnd(t: number): void {
    const obj = graphicById(this.scenario, this.scenario.objectiveId);
    if (obj && obj.pts.length > 0 && obj.radiusM !== undefined) {
      const center = obj.pts[0];
      const radiusCm = obj.radiusM * 100;
      let blueInside = 0;
      let redInside = false;
      for (const u of this.units) {
        if (!u.alive) continue;
        if (this.grid.distCm({ x: u.x, y: u.y }, center) > radiusCm) continue;
        if (u.side === 0) { if (isGroundPlatoon(u)) blueInside++; } else redInside = true;
      }
      this.objClearTicks = redInside ? 0 : this.objClearTicks + 1;
      if (blueInside >= OBJ_BLUE_PLATOONS && this.objClearTicks >= OBJ_CLEAR_TICKS) {
        this.ended = 'seized';
        this.log.push(t, 'obj.seized', 0, { graphicId: obj.id, name: obj.name, platoons: blueInside }, { significant: true });
        return;
      }
    }

    const events = this.log.events;
    for (; this.logCursor < events.length; this.logCursor++) {
      if (events[this.logCursor].kind === 'culminated') this.culminated = true;
    }
    if (this.culminated) { this.ended = 'culminated'; return; }

    if (t >= this.scenario.timeLimitMin * TICKS_PER_MIN) { this.ended = 'time'; }
  }

  private recordTruth(t: number): void {
    this.truth.push({
      tick: t,
      units: this.units.map((u) => ({ id: u.id, x: u.x, y: u.y, strength: u.strength, alive: u.alive })),
    });
  }
}

/** Convenience re-export so consumers of the worker protocol get the event type from one place. */
export type { Event };
